import {useBaseComponent} from "../../../base/base-component";
import React from "react";
import {IAssetSelect, IState} from "./type/asset-select-types";
import {message} from "antd";
import AssetSelectView from "./asset-select.view";
import {getAssetList} from "./action/asset-select.action";

const AssetSelect: React.FC<IAssetSelect> = (props) => {
    React.useEffect(() => {
        // Whenever categoryId changes: Reset page number to 0
        updateState('internal.pagination.pageNumber', 0);
        onInit();
    }, [props.categoryId]);

    const {state, updateState} = useBaseComponent<{}, IState>({}, {
        initialState: () => ({
            data: {
                assetList: []
            },
            internal: {
                maxItems: 30,
                searchQuery: '',
                pagination: {
                    hasNext: false,
                    hasPrevious: false,
                    isLoading: false,
                    pageSize: 10,
                    pageNumber: 0,
                }
            }
        }),
    });

    async function onInit() {
        const res = await getAssetList(
            '',
            state.internal.pagination.pageSize,
            0,
            props.categoryId
        );

        updateState('data.assetList', res);

        updateState('internal.pagination', {
            ...state.internal.pagination,
            hasNext: res.length === state.internal.pagination.pageSize,
            pageNumber: 0
        });
    }

    function handleChange(id: string) {
        updateState('internal.categoryId', id);
        props.onChange?.(id);
    }

    async function loadMoreData() {
        updateState('internal.pagination.isLoading', true);
        const nextPageNumber = state.internal.pagination.pageNumber + 1;
        try {
            const res = await getAssetList(
                state.internal.searchQuery,
                state.internal.pagination.pageSize,
                nextPageNumber,
                props.categoryId
            );

            let merged = [...state.data.assetList, ...res];
            let uniqueAssets = Array.from(
                new Map(merged.map((asset: any) => [asset.id, asset])).values()
            );
            // if docs length is more than maxItems then keep latest items
            if (uniqueAssets.length > state.internal.maxItems) {
                uniqueAssets = uniqueAssets.slice(uniqueAssets.length - state.internal.maxItems);
            }

            updateState('internal.pagination', {
                ...state.internal.pagination,
                hasNext: res.length === state.internal.pagination.pageSize,
                pageNumber: nextPageNumber
            });

            updateState('data.assetList', uniqueAssets);

        } catch (e) {
            message.error('something went wrong')
            console.log('error occurred while loading more data', e)
        } finally {
            updateState('internal.pagination.isLoading', false);
        }

    }

    async function loadPreviousData() {
        updateState('internal.pagination.isLoading', true);
        const previousPageNumber = state.internal.pagination.pageNumber - 1;
        if (previousPageNumber < 0 || state.data.assetList.length < state.internal.pagination.pageNumber) {
            updateState('internal.pagination.hasPrevious', false)
            return;
        }
        try {
            const res = await getAssetList(
                state.internal.searchQuery,
                state.internal.pagination.pageSize,
                previousPageNumber,
                props.categoryId
            );

            let merged = [...res, ...state.data.assetList];
            let uniqueAssets = Array.from(
                new Map(merged.map((asset: any) => [asset.id, asset])).values()
            );
            // if docs length is more than maxItems then keep latest items
            if (uniqueAssets.length > state.internal.maxItems) {
                uniqueAssets = uniqueAssets.slice(0, state.internal.maxItems);
            }
            updateState('internal.pagination', {
                ...state.internal.pagination,
                hasPrevious: true,
                hasNext: res.length === state.internal.pagination.pageSize,
                pageNumber: previousPageNumber
            });

            updateState('data.assetList', uniqueAssets);

        } catch (e) {
            message.error('something went wrong')
            console.log('error occurred while loading more data', e)
        } finally {
            updateState('internal.pagination.isLoading', false);
        }
    }

    async function handleSearch(searchQuery: string) {
        const searchText = searchQuery.trim();

        updateState('internal.searchQuery', searchText);

        const res = await getAssetList(
            searchText,
            state.internal.pagination.pageSize,
            0,
            props.categoryId
        );

        updateState('data.assetList', res);
    }

    function handleDropdownClose(isOpen: boolean) {
        if (!isOpen) {
            updateState('internal.searchQuery', '');
        }
    }

    return (
        <AssetSelectView
            disabled={props.disabled}
            includeAllEmployeeOption={props.includeAllSelectOption}
            placeholderText={props.placeholder}
            data={state.data.assetList}
            handleChange={handleChange}
            handleSearch={handleSearch}
            handleDropdownClose={handleDropdownClose}
            paginationControls={{
                isLoading: state.internal.pagination.isLoading,
                loadNext: loadMoreData,
                loadPrevious: loadPreviousData,
                hasNext: state.internal.pagination.hasNext,
                hasPrevious: state.internal.pagination.hasPrevious
            }}
        />
    )
}

export default AssetSelect;